<template>
  <div class="uk-card-body p-0 uk-grid-collapse uk-grid-large" uk-grid>
    <div class="uk-padding-small position-relative uk-width-1-5@m">
      <h5><i class="uil-table"/> {{ $t('general.add_with_excell') }}</h5>

      <!-- File Upload Area -->
      <div class="file-upload-container mb-3">
        <div :class="{'file-upload-disabled': isLoading, 'file-upload-has-file': fileInfo.file}"
             class="file-upload-area">
          <input
              id="fileToUpload"
              ref="fileInput"
              :disabled="isLoading"
              accept=".xls, .xlsx"
              class="file-upload-input"
              name="fileToUpload"
              type="file"
              @change="setFile"
          />
          <div class="file-upload-content">
            <i class="uil-cloud-upload file-upload-icon"/>
            <p class="file-upload-text">
              {{ fileInfo.file ? fileInfo.name : $t('general.excel_drag_drop') }}
            </p>
            <span v-if="fileInfo.file" class="file-info">
              {{ displayedRowCount }} {{ $t('general.survey_question') }}
            </span>
          </div>
        </div>

        <button class="btn btn-info template-download-btn" type="button" @click="exportSampleFile">
          <i class="uil-download-alt"/> {{ $t('general.download_excel_template') }}

          <i class="uil-info-circle ml-1"
             style="font-size: 1rem"
             :uk-tooltip="`title: ${$t('general.excel_template_download_info')}; pos: bottom-center`"
          />
        </button>


      </div>
    </div>

    <!-- Right Column - Data Display -->
    <div class="uk-padding-small uk-width-expand d-flex flex-column justify-content-between"
         style="border-left: 1px solid #ccc">
      <!-- Add button - displayed at top right when data is loaded -->
      <div v-if="displayedRows.length > 0" class="text-right mb-3">
        <button class="add-btn uk-button" type="button" @click="addImportedData">
          <i class="uil-plus-circle"/> {{
            is_added ? $t('general.questions_imported') : $t('general.import_questions')
          }}
        </button>
      </div>

      <default-loading v-if="isLoading"/>
      <div v-else class="uk-position-relative my-4 text-center">
        <div v-if="displayedRows.length === 0" class="empty-data-message">
          <i class="uil-file-upload-alt empty-data-icon"></i>
          <h4>{{ $t('general.add_with_excel') }}</h4>
        </div>
        <div v-else class="table-responsive">
          <table class="table align-items-center">
            <thead>
            <tr>
              <th scope="col"></th> <!-- New column for action buttons -->

              <th v-for="(header, index) in tableHeaders" :key="index" scope="col">
                {{ header }}{{ '*' }}
              </th>
              <th v-if="tableHeaders.length" scope="col">{{ $t('general.transactions') }}</th>
              <!-- New column for action buttons -->
            </tr>
            </thead>
            <tbody class="list">
            <tr v-for="(item, index) in displayedRows" :key="index">
              <td>{{ index + 1 }}</td>
              <td v-for="(header, colIndex) in tableHeaders" :key="colIndex">
                {{ item[colIndex] }}
              </td>
              <td>
                <button class="delete-btn uk-button uk-button-danger uk-button-small" type="button"
                        @click="removeItem(index)">
                  <i class="uil-trash-alt"/>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="displayedRows.length > 0" class="text-right mb-3">
        <button class="add-btn uk-button" type="button" @click="addImportedData">
          <i class="uil-plus-circle"/> {{
            is_added ? $t('general.questions_imported') : $t('general.import_questions')
          }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import axios from "axios";
import moment from "moment/moment";
import readXlsxFile from "read-excel-file";

export default {
  name: "ExcelUploader",
  components: {DefaultLoading},

  props: {
    // API endpoint for downloading sample Excel
    sampleExcelEndpoint: {
      type: String,
      required: true
    },
    // Default file name for downloaded sample
    sampleFileName: {
      type: String,
      default: "excel_sample"
    },
    excelHasHeader: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      errorMessages: [],
      isLoading: false,
      file: null,
      file_value: null,
      importedExam: null,
      processedRows: [], // This will hold our actual data rows
      fileInfo: {
        name: "",
        count: 0,
        file: null,
      },
      is_added: false,
      tableHeaders: []
    };
  },

  computed: {
    //(excluding header row)
    displayedRows() {
      return this.processedRows || [];
    },

    displayedRowCount() {
      this.is_added = false
      return this.displayedRows.length;
    }
  },

  methods: {
    resetFileInfo() {
      this.is_added = false;
      this.fileInfo.file = null;
      this.importedExam = null;
      this.processedRows = [];
      this.tableHeaders = [];

      // Use vue_refs instead of document.getElementById, better solutıion
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    setFile(e) {
      this.file_value = e.target.value;
      this.file = e.target.files[0];
      this.excelToJSON();
    },

    excelToJSON() {
      if (this.file) {
        this.isLoading = true;
        const fileName = this.file_value ? this.file_value.split(/(\\|\/)/g).pop() : "";

        this.fileInfo.file = this.file;
        readXlsxFile(this.file).then((rows) => {
          if (rows.length < 2) {
            this.errorMessages.push(this.$t("general.excel_empty_or_invalid"));
            this.$emit('import-error', this.$t("general.excel_empty_or_invalid"));
            this.isLoading = false;
            return;
          }

          if (this.excelHasHeader) {
            this.tableHeaders = rows[0];
            this.processedRows = rows.slice(1).filter(row => row && row.length > 0);
          } else {
            this.processedRows = rows
          }

          // Store full data for other operations if needed
          this.importedExam = rows;

          this.fileInfo.name = fileName;
          this.fileInfo.count = this.processedRows.length;
          this.is_added = false;
          this.isLoading = false;

        }).catch(error => {
          this.errorMessages.push(this.$t("general.excel_read_error"));
          this.$emit('import-error', error);
          this.isLoading = false;
        });
      }
    },

    removeItem(index) {
      this.processedRows.splice(index, 1);

      this.fileInfo.count = this.processedRows.length;

      if (this.processedRows.length === 0) {
        this.resetFileInfo();
      }
    },

    addImportedData() {
      this.$emit('imported', this.processedRows);

      this.is_added = true;

      this.$emit('add-success', {
        message: `${this.processedRows.length} kayıt başarıyla eklendi.`
      });
    },

    exportSampleFile(event) {
      event.preventDefault();

      this.isLoading = true;
      const config = {
        responseType: "blob",
        headers: this.apiHeaders,
      };

      axios.post(this.sampleExcelEndpoint, [], config)
          .then((response) => {
            const blob = new Blob([response.data]);
            const fileURL = window.URL.createObjectURL(blob);
            const fileLink = document.createElement("a");

            fileLink.style.display = "none";
            fileLink.href = fileURL;
            const day = moment().format("YYYYMMDDHHMMSS");
            const fileName = `${this.sampleFileName}_${day}.xlsx`;
            fileLink.setAttribute("download", fileName);

            document.body.appendChild(fileLink);
            fileLink.click();

            setTimeout(() => {
              document.body.removeChild(fileLink);
              window.URL.revokeObjectURL(fileURL);
              this.isLoading = false;
            }, 100);
          })
          .catch((error) => {
            this.errorMessages.push(this.$t("general.excel_template_download_error"));
            this.$emit('download-error', error);
            this.isLoading = false;
          });
    }
  }
};
</script>

<style scoped>
.file-upload-container {
  width: 100%;
  margin-bottom: 20px;
}

.file-upload-area {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 30px 20px;
  text-align: center;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 15px;
  overflow: hidden;
}

.file-upload-area:hover {
  border-color: #ae71ff;
  background-color: #f5f0ff;
}

.file-upload-disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.file-upload-has-file {
  border-color: #28a745;
  background-color: #f0fff4;
}

.file-upload-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.file-upload-content {
  position: relative;
  z-index: 1;
}

/* New styles for buttons */
.delete-btn {
  font-size: 0.85rem;
}

.delete-btn:hover {
  color: white;
}

.add-btn {
  padding: 6px 15px;
  font-size: 0.95rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.add-btn:hover {
  background-color: #218838;
}

.file-upload-text {
  word-wrap: break-word;
}

.empty-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #ddd;
  margin: 20px 0;
  position: relative;
}

.empty-data-icon {
  font-size: 4rem;
  color: #5864FF;
  margin-bottom: 15px;
}

.empty-data-message h4 {
  color: #343a40;
  margin-bottom: 10px;
  font-weight: 600;
}

.empty-data-message p {
  color: #6c757d;
  text-align: center;
  line-height: 1.5;
}
</style>